import type { ResponseWrapper } from '~/services/api/interfaces/Common'
import type { DashboardPartner } from '../interfaces/Partner'
import type { PartnerUpdateRequest, PartnerSummaryDto } from '~/services/api/generated'
import * as operations from '~/services/api/generated'
import { PartnerUpdate } from '~/services/api/contracts/requests/PartnerUpdate'
import { Normalization } from '~/utils/normalization'

const normalizeDashboardPartner = (value: PartnerSummaryDto): DashboardPartner => {
  return Normalization.omitUndefined({
    name: value.name,
    description: value.description ?? undefined,
    partner_type: value.partner_type,
    legal_entity_type: value.legal_entity_type,
    gratuity_percentage: value.gratuity_percentage,
    has_custom_gratuity_percentage: value.has_custom_gratuity_percentage
  })
}

const projectDashboardPartnerResponse = (
  response: operations.PartnerShowSuccess
): ResponseWrapper<DashboardPartner | null> => {
  return {
    data: response.data === null ? null : normalizeDashboardPartner(response.data)
  }
}

export function usePartnerService() {
  const baseURL = `${useRuntimeConfig().public.apiBaseUrl}/api/dashboard`
  const partnerURL = `${baseURL}/partner`
  const client = useApiRequest()

  return {
    /**
     * Get the current partner (account) for the authenticated user.
     */
    show: async () => {
      const response: unknown = await client(partnerURL, { method: 'GET' })
      const success = operations.PartnerShowSuccessSchema.parse(response)
      return projectDashboardPartnerResponse(success)
    },

    /**
     * Update the current partner account.
     */
    update: async (body: PartnerUpdateRequest) => {
      const request = PartnerUpdate({}).parse(body)
      const response: unknown = await client(partnerURL, {
        method: 'PUT',
        body: request
      })
      const success = operations.PartnerUpdateSuccessSchema.parse(response)
      return success
    },

    /**
     * Delete the current partner account.
     */
    destroy: async (): Promise<void> => {
      operations.PartnerDestroySuccessSchema.parse(await client(partnerURL, { method: 'DELETE' }))
    }
  }
}
